import React from "react"
import Select from "react-select"
import { Label } from "reactstrap"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import { capitalize } from "lodash"
import { personaFilterOptions } from "./personas"
import { withFloatingSurface } from "components/Common/selectSurface"

const levelsOptions = [4, 5, 6, 7, 8, 9].map(value => ({
  label: value,
  value,
}))

const genderOptions = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
]

const planOptions = [
  { label: "Free", value: "free" },
  { label: "Premium", value: "premium" },
]

const ageOptions = Array.from({ length: 85 }, (value, index) => ({
  label: index + 11,
  value: index + 11,
}))

const statusOptions = [
  { label: "Activated", value: "activated" },
  { label: "Blocked", value: "blocked" },
  { label: "Pending", value: "pending" },
  { label: "Deleted", value: "deleted" },
]

const selectedOption = (options, value) =>
  options.find(option => String(option.value) === String(value)) || null

// A location kept from the profile breadcrumb shows before its options are read, without a count.
const locationValueOption = value => {
  const [city, country] = String(value).split(", ")
  return { value, label: `${city === "null" ? "All Cities" : city}, ${country}` }
}

const FilterField = ({
  label,
  name,
  options,
  value,
  onChange,
  placeholder,
  toOption,
  ...selectProps
}) => (
  <div className="player-filter-field">
    <Label htmlFor={`player-filter-${name}`}>{label}</Label>
    <Select styles={withFloatingSurface()}
      inputId={`player-filter-${name}`}
      placeholder={placeholder || "All"}
      name={name}
      onChange={option => onChange(name, option)}
      options={options}
      value={selectedOption(options, value) || (toOption && value ? toOption(value) : null)}
      isClearable
      classNamePrefix="player-filter-select"
      {...selectProps}
    />
  </div>
)

const FilterList = ({
  advancedFilters,
  filters,
  locations,
  locationsCurrent,
  locationsLoading,
  onLocationMenuOpen,
  setFilters,
  settings,
}) => {
  const locationOptions = ((locationsCurrent && locations) || []).map(location => ({
    label: `${location.city ?? "All Cities"}, ${location.country} (${
      location.total
    })`,
    value: `${location.city ?? "null"}, ${location.country}`,
  }))
  const activeSports = (settings?.activeSports || []).map(sport => ({
    label: capitalize(sport),
    value: sport,
  }))
  const [minAge = "0", maxAge = "100"] = filters.age
    ? String(filters.age).split(",")
    : []

  const handleFilter = (name, option) => {
    const updatedFilters = { ...filters }

    if (["minAge", "maxAge"].includes(name)) {
      const age = [
        name === "minAge" ? option?.value ?? 0 : Number(minAge),
        name === "maxAge" ? option?.value ?? 100 : Number(maxAge),
      ]

      if (age[0] === 0 && age[1] === 100) {
        delete updatedFilters.age
      } else {
        updatedFilters.age = age.join()
      }
    } else if (option) {
      updatedFilters[name] = option.value
    } else {
      delete updatedFilters[name]
    }

    setFilters(updatedFilters)
  }

  return (
    <div className="player-filters">
      <div className="player-filters-primary">
        <FilterField
          label="Sport"
          name="sportType"
          options={activeSports}
          value={filters.sportType}
          onChange={handleFilter}
        />
        <FilterField
          label="Singles level"
          name="gameLevel[singles]"
          options={levelsOptions}
          value={filters["gameLevel[singles]"]}
          onChange={handleFilter}
        />
        <FilterField
          label="Doubles level"
          name="gameLevel[doubles]"
          options={levelsOptions}
          value={filters["gameLevel[doubles]"]}
          onChange={handleFilter}
        />
        <FilterField
          label="Location"
          name="location"
          options={locationOptions}
          value={filters.location}
          onChange={handleFilter}
          toOption={locationValueOption}
          onMenuOpen={onLocationMenuOpen}
          isLoading={locationsLoading}
          loadingMessage={() => "Loading locations…"}
        />
        <FilterField
          label="Status"
          name="status"
          options={statusOptions}
          value={filters.status}
          onChange={handleFilter}
        />
        {/* PRD 16 LR16 (LR10 verdict L-A): the account's exact persona set */}
        <FilterField
          label="Persona"
          name="personas"
          options={personaFilterOptions}
          value={filters.personas}
          onChange={handleFilter}
        />
      </div>

      {advancedFilters && (
        <div className="player-filters-secondary">
          <FilterField
            label="Gender"
            name="gender"
            options={genderOptions}
            value={filters.gender}
            onChange={handleFilter}
          />
          <div className="player-filter-field player-age-filter">
            <Label>Age range</Label>
            <div className="player-age-inputs">
              <Select styles={withFloatingSurface()}
                inputId="player-filter-minAge"
                aria-label="Minimum age"
                placeholder="Min"
                name="minAge"
                onChange={option => handleFilter("minAge", option)}
                options={ageOptions}
                value={selectedOption(ageOptions, minAge)}
                isClearable
                classNamePrefix="player-filter-select"
              />
              <span>to</span>
              <Select styles={withFloatingSurface()}
                inputId="player-filter-maxAge"
                aria-label="Maximum age"
                placeholder="Max"
                name="maxAge"
                onChange={option => handleFilter("maxAge", option)}
                options={ageOptions}
                value={selectedOption(ageOptions, maxAge)}
                isClearable
                classNamePrefix="player-filter-select"
              />
            </div>
          </div>
          <FilterField
            label="Membership"
            name="membership"
            options={planOptions}
            value={filters.membership}
            onChange={handleFilter}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = ({ players, session }) => ({
  locations: players.playersLocations,
  locationsLoading: players.playersLocationsLoading,
  settings: session.settings,
})

export default connect(mapStateToProps)(withRouter(FilterList))
